import { useSWR } from '@tiger/swr';
import { Empty, Skeleton } from 'antd';
import type { FC } from 'react';
import { Link } from 'react-router-dom';
import ErrorBox from 'components/ErrorBox';
import security from 'stores/security';
import API from 'utils/API';
import { formatDate, formatMoney } from 'utils/format';
import { getMaskText } from 'utils/util';
import { RecurringScheduleStatus } from '../constants';
import type { PeriodPlanListItem } from '../types';
import styles from './style.scss';

interface Props {
    isIRA: boolean;
}

const ScheduleList: FC<Props> = ({ isIRA }) => {
    const {
        isLoading,
        data: scheduleList,
        error,
        mutate
    } = useSWR<PeriodPlanListItem[]>(
        {
            url: API.TRADE.funding.getDepositAchPlanList(),
            params: {
                account_id: security.currentAccountId,
                customer_id: security.currentCustomerId,
                seg_type: 'SEC',
                bind_type: 'ACH'
            }
        },
        {
            dataChain: res => res.data
        }
    );

    if (isLoading) {
        return <Skeleton active />;
    }

    if (!scheduleList && error) {
        return <ErrorBox error={error} onClick={() => mutate()} />;
    }

    return (
        <div className={styles.pageRoot}>
            <h3 className={styles.pageTitle}>
                {isIRA ? __('Recurring Contribution Schedule') : __('Recurring Deposit Schedule')}
            </h3>
            {scheduleList?.length ? (
                <div className={styles.depositPlanList}>
                    {scheduleList.map(plan => {
                        const { status, id, amount, currency, bankAccount, nextExecuteTime } = plan;

                        return (
                            <Link to={`/profile/deposit/ach/recurring/schedules/${id}`} key={id}>
                                <div className={styles.depositPlanListItem}>
                                    <img
                                        className={styles.itemIcon}
                                        src={RecurringScheduleStatus[status]?.icon}
                                        alt="icon"
                                    />
                                    <div className={styles.itemContent}>
                                        <div className={styles.header}>
                                            <div className={styles.amount}>
                                                <span>{formatMoney(amount)}</span>
                                                {currency}
                                            </div>
                                            <div className={styles.account}>{getMaskText(bankAccount)}</div>
                                        </div>
                                        <div className={styles.content}>
                                            <div className={styles.time}>
                                                <span>
                                                    {isIRA ? __('Next Contribution Time') : __('Next Deposit Time')}
                                                </span>
                                                <span>{formatDate(nextExecuteTime, 'YYYY-MM-DD HH:mm')}</span>
                                            </div>
                                            <div
                                                className={styles.status}
                                                style={{
                                                    color: `${RecurringScheduleStatus[status]?.color ?? 'inherit'}`
                                                }}>
                                                {RecurringScheduleStatus[status]?.text || status}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            ) : (
                <Empty description={__('暂无数据')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
        </div>
    );
};

export default ScheduleList;
